import { useState, useRef, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { Box, Stack } from '@mui/material'
import { GoogleMap, InfoWindow, Marker, OverlayView } from '@react-google-maps/api'
import TileWrapper from 'components/pages/dis/components/TileWrapper'
import { SummaryMapFilters } from './SummaryMapFilters'
import { TASK_MAP_SETTING_TYPES } from '../../../summaries/utils/constant'

import { parseAs } from 'utils/functions/doformsDateUtil'
import { getFilteredRows } from '../../../../../../data/dataHelpers'
import { isEmpty, isEqual } from 'lodash'
import { DATE_DATAGRID_FILTER_OPERATORS } from 'utils/functions/helpers'

const defaultMapState = {
	center: { lat: 33.753746, lng: -84.38633 },
	zoom: 11,
}

const circleSvgPath = 'M0,6 a6,6 0 0 1 12,0 a6,6 0 0 1 -12,0'
const MARKER_CIRCLE_SCALE_NORMAL = 1
const MARKER_CIRCLE_SCALE_ACTIVE = 1.5
const CIRCLE_ANCHOR_X = 6
const CIRCLE_ANCHOR_Y = 6
const GPS_LOCATION_COLUMN_KEY = 'GPS_Location'

const DEFAULT_MONO_ZOOM_LEVEL = 15

export function SummaryMapInfo(props) {
	const {
		tile,
		selectedRow,
		displayGridRows,
		settings,
		handleSubmitSetting,
		dateFilterColumn,
		useTileWrapper = true,
	} = props

	const { environment } = useSelector((state) => state)

	const { tileWidth, locationData, taskMapAddressField, taskMapBubbleFields } = useMemo(
		() => settings,
		[settings]
	)

	const [hoverKey, setHoverKey] = useState(null)

	const [settingsOpen, setSettingsOpen] = useState(false)
	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const gridTileRef = useRef(null)

	const [fromDate, setFromDate] = useState(null)
	const [toDate, setToDate] = useState(null)
	const [selectedMarker, setSelectedMarker] = useState(null)

	const geocoder = useMemo(() => new window.google.maps.Geocoder(), [])

	const userOffset = useMemo(() => {
		return environment.userCurrent.time.timezone.offset || 0
	}, [environment])

	useEffect(() => {
		const userMoment = moment.utc(locationData.fromDate).utcOffset(userOffset)
		setFromDate(userMoment)
	}, [locationData.fromDate])

	useEffect(() => {
		// If To date is empty, use now
		if (!locationData.toDate) {
			const currentMoment = moment().endOf('day')
			const userMoment = parseAs(currentMoment, userOffset)
			setToDate(userMoment)
			return
		}
		setToDate(moment.utc(locationData.toDate))
	}, [locationData.toDate, userOffset])

	const filteredRows = useMemo(() => {
		const filterModel = []
		if (!isEmpty(dateFilterColumn)) {
			filterModel.push({
				field: dateFilterColumn.name,
				operator: DATE_DATAGRID_FILTER_OPERATORS.IS_ON_OR_AFTER,
				value: fromDate,
				type: dateFilterColumn.type,
				id: 'dateFromFilter',
			})

			filterModel.push({
				field: dateFilterColumn.name,
				operator: DATE_DATAGRID_FILTER_OPERATORS.IS_ON_OR_BEFORE,
				value: toDate,
				type: dateFilterColumn.type,
				id: 'dateToFilter',
			})
		}

		if (selectedRow) {
			return getFilteredRows(filterModel, [selectedRow])
		}
		return getFilteredRows(filterModel, displayGridRows)
	}, [fromDate, toDate, dateFilterColumn, selectedRow, displayGridRows])

	const addressFromRows = useMemo(() => {
		return filteredRows.reduce((prevValue, currentRow) => {
			if (taskMapAddressField === GPS_LOCATION_COLUMN_KEY) {
				const lat = currentRow?.GPS_LocationLATITUDE
				const lon = currentRow?.GPS_LocationLONGITUDE

				if (lat != null && lon != null) {
					prevValue.push({
						latitude: lat,
						longitude: lon,
					})
				}

				return prevValue
			}

			const address = currentRow?.[taskMapAddressField]
			if (!address) {
				return prevValue
			}
			prevValue.push(address)
			return prevValue
		}, [])
	}, [taskMapAddressField, filteredRows])

	const [allLocations, setAllLocations] = useState([])

	useEffect(() => {
		if (isEmpty(addressFromRows)) {
			if (!isEmpty(allLocations)) {
				setAllLocations([])
			}
			return
		}
		const isFromGpsColumn =
			typeof addressFromRows[0] === 'object' &&
			addressFromRows[0] !== null &&
			'latitude' in addressFromRows[0] &&
			'longitude' in addressFromRows[0]

		if (isFromGpsColumn) {
			const newLocations = addressFromRows.map((gps) => ({
				lat: gps.latitude,
				lng: gps.longitude,
			}))

			if (!isEqual(newLocations, allLocations)) {
				setAllLocations(newLocations)
			}

			return
		}

		const getAddressFromGeocode = (address) => {
			return new Promise((resolve) => {
				geocoder.geocode({ address }, function (results, status) {
					if (status === window.google.maps.GeocoderStatus.OK) {
						const lat = results[0].geometry.location.lat()
						const lng = results[0].geometry.location.lng()
						resolve({ lat, lng })
					} else {
						resolve(null)
					}
				})
			})
		}

		Promise.all(addressFromRows.map((address) => getAddressFromGeocode(address))).then((res) => {
			const newLocations = res.filter(Boolean)
			if (!isEqual(newLocations, allLocations)) {
				setAllLocations(newLocations)
			}
		})
	}, [addressFromRows])

	const mapWraperRef = useRef(null)
	const [mapRef, setMapRef] = useState(null)
	const [center, setCenter] = useState(defaultMapState.center)
	const [zoom, setZoom] = useState(defaultMapState.zoom)

	const handleOnLoad = (map) => {
		if (!map) return

		// Store a reference to the google map instance in state
		setMapRef(map)
		map.addListener('zoom_changed', () => {
			console.log('Zoom level:', map.getZoom())
		})
	}

	const fitBounds = (map, allLocations) => {
		if (!map) return

		if (isEmpty(allLocations)) {
			setCenter(defaultMapState.center)
			setZoom(defaultMapState.zoom)

			// Force map to reapply the zoom
			map.setZoom(defaultMapState.zoom)
			return
		}

		const bounds = new window.google.maps.LatLngBounds()

		allLocations.forEach((location) => bounds.extend(location))

		if (allLocations.length === 1) {
			setZoom(DEFAULT_MONO_ZOOM_LEVEL)

			// Force map to reapply the zoom
			map.setZoom(DEFAULT_MONO_ZOOM_LEVEL)
		} else {
			mapRef.fitBounds(bounds)
		}

		const centerFromBounds = bounds.getCenter()
		if (!centerFromBounds) return
		setCenter(centerFromBounds)
	}

	useEffect(() => {
		if (mapRef) {
			fitBounds(mapRef, allLocations)
		}
	}, [mapRef, allLocations])

	return useTileWrapper ? (
		<TileWrapper title={'Map'} onSettingClick={handleOpenDialog} ref={mapWraperRef}>
			{/* <TaskMapDatagridSettingDialog
				tileElementWidth={gridTileRef?.current?.clientWidth}
				defaultTileWidth={tileWidth}
				tile={tile}
				settings={settings}
				// isSubmitting={updateTileMutation.isLoading}
				open={settingsOpen}
				onClose={handleCloseDialog}
				onSubmit={handleSubmitSetting}
				taskMapSettingType={TASK_MAP_SETTING_TYPES.MAP}
				// onResizeTileWidth={handleResizeTileWidth}
			/> */}

			<GoogleMap
				ref={mapRef}
				onLoad={handleOnLoad}
				center={center}
				zoom={zoom}
				mapContainerStyle={{
					height: '100%',
					width: '100%',
				}}
				options={{
					minZoom: 1,
					gestureHandling: 'greedy',
				}}
			>
				{allLocations?.map((location, index) => {
					const key = `${index}_point`
					const markerIcon = {
						fillColor: '#ff0000',
						fillOpacity: 1,
						path: circleSvgPath,
						scale: hoverKey === key ? MARKER_CIRCLE_SCALE_ACTIVE : MARKER_CIRCLE_SCALE_NORMAL,
						strokeColor: '#ffffff',
						strokeWeight: 1,
						anchor: new window.google.maps.Point(CIRCLE_ANCHOR_X, CIRCLE_ANCHOR_Y),
					}
					return (
						<Marker
							key={key}
							position={location}
							icon={markerIcon}
							onMouseOver={() => setHoverKey(key)}
							onMouseOut={() => setHoverKey(null)}
							onClick={() => {
								setSelectedMarker({
									index,
									lat: location.lat,
									lng: location.lng,
									row: filteredRows[index],
								})
							}}
						/>
					)
				})}
				{selectedMarker && taskMapBubbleFields && (
					<OverlayView
						position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
						mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
					>
						<div
							style={{
								position: 'relative',
								transform: 'translate(-50%, -100%)',
								background: 'white',
								padding: '8px 12px',
								borderRadius: '10px',
								boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
								fontWeight: 'bold',
								maxWidth: '260px',
								fontSize: '14px',
								paddingRight: '25px',
							}}
						>
							<div
								onClick={() => setSelectedMarker(null)}
								style={{
									position: 'absolute',
									top: '4px',
									right: '6px',
									cursor: 'pointer',
									fontSize: '14px',
									fontWeight: 'bold',
									opacity: 0.7,
								}}
							>
								✕
							</div>

							<div>
								{taskMapBubbleFields.length === 0 ? (
									<>No fields selected</>
								) : (
									taskMapBubbleFields.map((field) => selectedMarker.row?.[field] ?? '').join(' │ ') // dấu đậm
								)}
							</div>
						</div>
					</OverlayView>
				)}
			</GoogleMap>
		</TileWrapper>
	) : (
		<GoogleMap
			ref={mapRef}
			onLoad={handleOnLoad}
			center={center}
			zoom={zoom}
			mapContainerStyle={{
				height: '100%',
				width: '100%',
			}}
			options={{
				minZoom: 4,
				gestureHandling: 'greedy',
			}}
		>
			{allLocations?.map((location, index) => {
				const key = `${index}_point`
				const markerIcon = {
					fillColor: '#ff0000',
					fillOpacity: 1,
					path: circleSvgPath,
					scale: hoverKey === key ? MARKER_CIRCLE_SCALE_ACTIVE : MARKER_CIRCLE_SCALE_NORMAL,
					strokeColor: '#ffffff',
					strokeWeight: 1,
					anchor: new window.google.maps.Point(CIRCLE_ANCHOR_X, CIRCLE_ANCHOR_Y),
				}
				return (
					<Marker
						key={key}
						position={location}
						icon={markerIcon}
						onMouseOver={() => setHoverKey(key)}
						onMouseOut={() => setHoverKey(null)}
						onClick={() => {
							setSelectedMarker({
								index,
								lat: location.lat,
								lng: location.lng,
								row: filteredRows[index],
							})
						}}
					/>
				)
			})}
			{selectedMarker && taskMapBubbleFields && (
				<OverlayView
					position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
					mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
				>
					<div
						style={{
							position: 'relative',
							transform: 'translate(-50%, -100%)',
							background: 'white',
							padding: '8px 12px',
							borderRadius: '10px',
							boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
							fontWeight: 'bold',
							maxWidth: '260px',
							fontSize: '14px',
						}}
					>
						<div
							onClick={() => setSelectedMarker(null)}
							style={{
								position: 'absolute',
								top: '4px',
								right: '6px',
								cursor: 'pointer',
								fontSize: '14px',
								fontWeight: 'bold',
								opacity: 0.7,
							}}
						>
							✕
						</div>

						<div>
							{taskMapBubbleFields.length === 0 ? (
								<>No fields selected</>
							) : (
								taskMapBubbleFields.map((field) => selectedMarker.row?.[field] ?? '').join(' │ ') // dấu đậm
							)}
						</div>
					</div>
				</OverlayView>
			)}
		</GoogleMap>
	)
}
